import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/logo.svg";
import { FaBars } from "react-icons/fa";
import { RxCross1 } from "react-icons/rx";
import { routesConfig } from "../Routes/routesConfig";
import useSignedIn from "../hooks/useSignedIn";
import { getHoverStyle, linkStyle, navButtonStyle } from "../help/Utils";
import MegaMenu from "./MegaMenu";
import { megaMenuData } from "../data/megaMenuData";
import { FaAngleUp, FaAngleDown } from "react-icons/fa";
import useLogout from "../hooks/useLogout";
import { IoIosArrowDown } from "react-icons/io";
import userImg from "../assets/user.png";
import { MdKeyboardArrowDown } from "react-icons/md";
import { initialsFromName } from "../utils/userInitials";

/**
 * Read the cached profile out of localStorage so the avatar can render
 * synchronously on mount. We intentionally tolerate any JSON shape
 * mismatch — if anything is off we just fall back to the default image.
 */
function readCachedUserProfile() {
  try {
    const raw = localStorage.getItem("userDetails");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

const Header = () => {
  const [isOpen, setIsOpen] = useState(false); // Mobile hamburger state
  const [megaMenuOpen, setMegaMenuOpen] = useState(false); // Desktop mega menu
  const [featuresAccordionOpen, setFeaturesAccordionOpen] = useState(false); // Mobile "Features" accordion
  // Subscribed, not read once during render: this used to capture the value
  // at mount, so a session cleared afterwards left the signed-in menu and
  // avatar on screen until a full navigation.
  const isLoggedIn = useSignedIn();
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const [selected, setSelected] = useState("Select option");
  const [userProfile, setUserProfile] = useState(() => readCachedUserProfile());

  /**
   * Keep the avatar in sync when other parts of the app write a fresh
   * `userDetails` entry (e.g. after first login or a profile update).
   * Storage events fire only across tabs; for same-tab updates we rely
   * on the existing pages persisting via localStorage.setItem and the
   * effect below reading on focus.
   */
  useEffect(() => {
    const refresh = () => setUserProfile(readCachedUserProfile());
    window.addEventListener("storage", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  const userInitials = initialsFromName(
    userProfile?.fullName,
    userProfile?.email || localStorage.getItem("email"),
  );

  // ✅ Fixed spelling here
  const options = ["View Profile", "Payment History", "Logout"];

  const headerRef = useRef(null);
  const logout = useLogout();
  const navigate = useNavigate();

  // Toggles for hamburger
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Toggle desktop mega menu
  const toggleMegaMenu = () => {
    setMegaMenuOpen((prev) => !prev);
  };

  // Close mega menu if clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setMegaMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <header ref={headerRef} className="">
      {/* NAV (Desktop / Tablet) */}
      <div className="custom-container mx-auto">
        <nav className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" onClick={() => setIsOpen(false)}>
            <img src={logo} className="w-[250px]" alt=" Logo" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex flex items-center">
            <ul className="flex space-x-7 main-menu">
              {routesConfig
                .filter(
                  (item) =>
                    item.showInNav &&
                    !item.isButton &&
                    (item.authVisible === undefined ||
                      (isLoggedIn && item.authVisible === "login") ||
                      (!isLoggedIn && item.authVisible === "logout"))
                )
                .map((item) => {
                  if (item.name === "Our Services") {
                    // On desktop, "Features" toggles the mega menu
                    return (
                      <li
                        key={item.name}
                        style={linkStyle}
                        className="cursor-pointer flex items-center text-[#1B365D] megamenu__li "
                        onClick={toggleMegaMenu}
                      >
                        <span>{item.name}</span> <MdKeyboardArrowDown />
                      </li>
                    );
                  } else {
                    return (
                      <HoverableLink
                        key={item.name}
                        path={item.path}
                        name={item.name}
                        isButton={false}
                        externalUrl={item.externalUrl}
                      />
                    );
                  }
                })}
              {/* Manual Logout + User Dropdown */}
              {isLoggedIn && (
                <div className="flex items-center">
                  <div className="relative inline-block text-left">
                    {/* Button */}
                    <button
                      onClick={() => setIsOpenDropdown(!isOpenDropdown)}
                      className="flex items-center justify-between"
                      aria-label={
                        userProfile?.fullName
                          ? `Account menu for ${userProfile.fullName}`
                          : "Account menu"
                      }
                    >
                      {userInitials ? (
                        <span
                          className="w-8 h-8 me-3 rounded-full bg-[#1B365D] text-white font-semibold text-sm flex items-center justify-center select-none"
                          aria-hidden="true"
                          title={userProfile?.fullName || userProfile?.email || undefined}
                        >
                          {userInitials}
                        </span>
                      ) : (
                        <img src={userImg} className="w-8 h-8 me-3" alt="user" />
                      )}
                      <IoIosArrowDown
                        className={`ml-2 transition-transform ${isOpenDropdown ? "rotate-180" : "rotate-0"
                          }`}
                      />
                    </button>

                    {/* Dropdown menu */}
                    {isOpenDropdown && (
                      <div className="absolute right-0 z-10 w-[180px] mt-2 bg-white border border-gray-200 rounded-xl shadow-lg">
                        {options.map((option, idx) => (
                          <div
                            key={idx}
                            onClick={() => {
                              setSelected(option);
                              setIsOpenDropdown(false);

                              if (option === "View Profile") {
                                navigate("/profile");
                              } else if (option === "Payment History") {
                                navigate("/payment-history");
                              } else if (option === "Logout") {
                                logout();
                              }
                            }}
                            className="px-4 py-4 text-gray-700 cursor-pointer hover:bg-gray-100"
                          >
                            {option}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </ul>
            {/* Desktop Buttons */}
            <ul className="flex space-x-6 ">
              {routesConfig
                .filter(
                  (item) =>
                    item.showInNav &&
                    item.isButton &&
                    (item.authVisible === undefined ||
                      (isLoggedIn && item.authVisible === "login") ||
                      (!isLoggedIn && item.authVisible === "logout"))
                )
                .map((item) => (
                  <HoverableLink
                    key={item.name}
                    path={item.path}
                    name={item.name}
                    isButton={true}
                    externalUrl={item.externalUrl}
                  />
                ))}
            </ul>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={toggleMenu}
            className="text-2xl text-black focus:outline-none lg:hidden"
            aria-label="Toggle Menu"
          >
            {isOpen ? <RxCross1 /> : <FaBars />}
          </button>
        </nav>
      </div>

      {/* Desktop MegaMenu */}
      <MegaMenu
        isOpen={megaMenuOpen}
        menuData={megaMenuData}
        onLinkClick={() => setMegaMenuOpen(false)}
      />

      {/* MOBILE OVERLAY (Hamburger Menu) */}
      <div
        className={`
          lg:hidden
          fixed top-0 left-0
          w-full h-full
          bg-white z-50
          transition-transform duration-300
          overflow-y-auto
          ${isOpen ? "translate-x-0" : "translate-x-full"}
        `}
      >
        {/* Overlay Header */}
        <div className="flex justify-between items-center p-5">
          <Link to="/" onClick={() => setIsOpen(false)}>
            <img src={logo} className="w-[150px]" alt=" Logo" />
          </Link>
          <button
            onClick={toggleMenu}
            className="text-2xl text-black focus:outline-none"
            aria-label="Close Menu"
          >
            <RxCross1 />
          </button>
        </div>

        {/* Overlay Links */}
        <ul className="flex flex-col gap-[2.5rem] mt-10 p-5 text-center">
          {routesConfig
            .filter(
              (item) =>
                item.showInNav &&
                item.isButton &&
                (item.authVisible === undefined ||
                  (isLoggedIn && item.authVisible === "login") ||
                  (!isLoggedIn && item.authVisible === "logout"))
            )
            .map((item) => {
              if (item.name === "Our Services") {
                return (
                  <li key={item.name} className="text-left">
                    <div
                      className="flex items-center justify-center md:justify-start cursor-pointer"
                      onClick={() =>
                        setFeaturesAccordionOpen((prev) => !prev)
                      }
                    >
                      <span style={linkStyle} className="mr-2">
                        {item.name}
                      </span>
                      <span className="text-xl">
                        {featuresAccordionOpen ? <FaAngleUp /> : <FaAngleDown />}
                      </span>
                    </div>

                    {featuresAccordionOpen && (
                      <ul className="mt-3 space-y-6 text-start pl-6 items-center">
                        {megaMenuData.map((feature) => (
                          <li key={feature.slug}>
                            <Link
                              to={`/features/${feature.slug}`}
                              onClick={() => setIsOpen(false)}
                              className="block md:text-lg font-bold hover:text-primaryColor"
                            >
                              {feature.mainTitle}
                            </Link>

                            <ul className="mt-2 pl-3 space-y-1">
                              {feature.subTitles.map((subtitle, i) => (
                                <li key={i}>
                                  <Link
                                    to={`/features/${feature.slug}`}
                                    onClick={() => setIsOpen(false)}
                                    className="hover:text-primaryColor"
                                  >
                                    {subtitle}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              } else {
                return (
                  <li key={item.name} onClick={() => setIsOpen(false)}>
                    <HoverableLink
                      path={item.path}
                      name={item.name}
                      isButton={false}
                      externalUrl={item.externalUrl}
                      overlay
                    />
                  </li>
                );
              }
            })}

          {/* Button Items */}
          {routesConfig
            .filter(
              (item) =>
                item.showInNav &&
                item.isButton &&
                (item.authVisible === undefined ||
                  (isLoggedIn && item.authVisible === "login") ||
                  (!isLoggedIn && item.authVisible === "logout"))
            )
            .map((item) => (
              <li key={item.name} onClick={() => setIsOpen(false)}>
                <HoverableLink
                  path={item.path}
                  name={item.name}
                  isButton={true}
                  externalUrl={item.externalUrl}
                  overlay
                />
              </li>
            ))}
        </ul>
      </div>
    </header>
  );
};

/**
 * HoverableLink
 * Renders a React Router <Link> or an <a> for external URLs
 */
const HoverableLink = ({ path, externalUrl, name, isButton, overlay }) => {
  const [hover, setHover] = useState(false);
  const location = useLocation();

  const baseStyle = isButton ? navButtonStyle : linkStyle;
  const isActive = !externalUrl && location.pathname === path;

  const finalStyle = {
    ...baseStyle,
    ...getHoverStyle(isButton, hover, isButton),
    ...(overlay ? {} : {}),
  };

  if (isActive) {
    finalStyle.color = "#1B365D";
    finalStyle.borderBottom = "1px solid #005C78";
  }

  // External link vs internal link
  if (externalUrl) {
    return (
      <a
        href={externalUrl}
        style={finalStyle}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        target="_blank"
        className="btn-login"
        rel="noopener noreferrer"
      >
        {name}
      </a>
    );
  } else {
    return (
      <Link
        to={path}
        style={finalStyle}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {name}
      </Link>
    );
  }
};

export default Header;